import { addKnownTag } from '@store/redux/features/inventory/knownData';
import { RootState, useAppDispatch } from '@store/redux/store';
import { useMemo, useCallback, memo } from 'react';
import { useSelector } from 'react-redux';

import { ITagLimitWithDesc } from '@models/Tag.model';
import { AssignTag } from '@pages/Asset/components/Sidebar/components/Tags/components/AssignTag/AssignTag';

import { Icon } from '@blueprintjs/core';
import { TagDialogHover } from '@components/TagDialog/TagDialogHover';
import { useHookstate } from '@hookstate/core';
import { Group } from '@mantine/core';
import { sortStr } from '@utils/sorting/non-ascii';

import { isNonNullable } from '@utils/isNonNullable';
import { TagListWithDelete } from '../TagListWithDelete';
import {
  AddMoreButton,
  BigInputDiv,
  GlobalHeader,
  InputList,
  TAG_SX,
} from '../styles';
import { PropsInputGlobal } from './types';
import {
  addTagsTo,
  calcTagProps,
  handleGlobalDeleteNew,
  onChangeGlobal,
  setForAllNew,
} from './utils';

type AssignTagsGroupProps = PropsInputGlobal<'tagsId'>;

export function AssignTagsGroup(props: AssignTagsGroupProps) {
  const { setFormV, tagsId, equipmentsCount, setGlobalFormV } = props;

  const openAssign = useHookstate(false);

  const dispatch = useAppDispatch();
  const knownTags = useSelector(
    (state: RootState) => state.knownData.knownTags,
  );

  const catTags = useMemo(() => {
    const tagEntries = Object.entries(tagsId);
    return tagEntries
      .filter(entry => entry[1].count === equipmentsCount)
      .map(entry => {
        return knownTags[entry[1].id] as ITagLimitWithDesc | undefined;
      })
      .filter(isNonNullable)
      .sort((a, b) => sortStr(a.name, b.name));
  }, [equipmentsCount, knownTags, tagsId]);
  const $assignTagsToAll = useCallback(assignTagsToAll, [tagsId]);
  return (
    <InputList className="tags">
      <BigInputDiv className="tags">
        <GlobalHeader>Assign Tags</GlobalHeader>
        <Group spacing={8} noWrap sx={TAG_SX} data-size={catTags.length}>
          <TagDialogHover
            tags={catTags}
            calcTagProps={calcTagProps}
            deleteHandler={handleGlobalDeleteNew}
            deleteHandlerArgs={[setGlobalFormV, setFormV]}
          >
            <TagListWithDelete
              tags={catTags}
              deleteHandler={handleGlobalDeleteNew}
              deleteHandlerArgs={[setGlobalFormV, setFormV]}
            />
          </TagDialogHover>
          <AddMoreButton
            className="new-icon"
            onClick={() => {
              openAssign.set(true);
            }}
          >
            <Icon icon="plus" color="#1371fe" />
            Assign Tag to all
          </AddMoreButton>
          <AssignTag
            open={openAssign}
            activeList={catTags}
            onConfirm={$assignTagsToAll}
          />
        </Group>
      </BigInputDiv>
    </InputList>
  );

  function assignTagsToAll($newTags: ITagLimitWithDesc[], done: VoidFunction) {
    dispatch(addKnownTag($newTags));
    const newTags = [...$newTags];
    newTags.sort((taga, tagb) => sortStr(taga.name, tagb.name));
    // Set global
    const countasMap = new Map(Object.entries(tagsId));
    newTags.forEach(tag => {
      countasMap.set(tag.id, { id: tag.id, count: equipmentsCount });
    });
    onChangeGlobal('tagsId', Object.fromEntries(countasMap), setGlobalFormV);
    // Set equipments while keeping it sorted.
    setForAllNew(
      'tagsId',
      current => addTagsTo(newTags, current ?? [], knownTags),
      setFormV,
    );

    done();
  }
}
export default memo(AssignTagsGroup);
